import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ProjectTask, CRMUser } from '../../types';
import StatusPill from './StatusPill';
import PriorityBadge from './PriorityBadge';
import { Check, Flag, GripVertical, ChevronRight, ArrowLeft, Calendar } from 'lucide-react';
import {
  ACTION_REQUIRED_EXTERNAL_SENTINEL,
  getActionRequiredShortLabel,
  isActionRequiredExternal,
} from '../../utils/actionRequiredDisplay';
import UserAvatar from '../ui/UserAvatar';
import { normalizeTaskAssignedTo } from '../../utils/projectTaskUsers';
import {
  formatRelativeLocalCalendarDay,
  isTaskDueDatePastLocalCalendar,
  toLocalDateInputValue,
} from '../../utils/taskSchedule';
import type { CommitTaskDatesHint } from '../../utils/taskDateCommit';
import { taskNeedsPmDateReview } from '../../utils/pmDateReview';

const PM_DATE_REVIEW_TIP =
  'These dates were generated when the project moved under contract. They are placeholders until the assigned project manager sets the real schedule.';

const PmDateReviewIcon: React.FC = () => {
  const wrapRef = useRef<HTMLSpanElement>(null);
  const [tip, setTip] = useState<{ top: number; left: number } | null>(null);

  const show = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 260;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    setTip({ top: rect.bottom + 6, left });
  }, []);

  return (
    <>
      <span
        ref={wrapRef}
        className="pm-task-date-review-wrap"
        onMouseEnter={show}
        onMouseLeave={() => setTip(null)}
        onFocus={show}
        onBlur={() => setTip(null)}
        tabIndex={0}
        aria-label={PM_DATE_REVIEW_TIP}
      >
        <Calendar size={12} className="pm-task-date-review" aria-hidden />
      </span>
      {tip && createPortal(
        <div
          className="pm-task-date-review-tip"
          role="tooltip"
          style={{ top: tip.top, left: tip.left }}
        >
          <strong>Placeholder dates</strong>
          <p>{PM_DATE_REVIEW_TIP}</p>
        </div>,
        document.body,
      )}
    </>
  );
};

interface TaskRowProps {
  task: ProjectTask;
  teamMembers: CRMUser[];
  onUpdate: (id: string, updates: Partial<ProjectTask>) => void;
  onSelect: (task: ProjectTask) => void;
  onToggleComplete: (task: ProjectTask) => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, task: ProjectTask) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, task: ProjectTask) => void;
  subtaskTotal?: number;
  subtaskDone?: number;
  hidePriority?: boolean;
  hideAction?: boolean;
  /** False for architect / supplier hub — date stays read-only. */
  datesEditable?: boolean;
  isSummaryParent?: boolean;
  onCommitTaskDates?: (taskId: string, startDate: Date, endDate: Date, hint: CommitTaskDatesHint) => void | Promise<void>;
}

const TaskRow: React.FC<TaskRowProps> = ({
  task, teamMembers, onUpdate, onSelect, onToggleComplete,
  draggable, onDragStart, onDragOver, onDrop,
  subtaskTotal, subtaskDone,
  hidePriority = false,
  hideAction = false,
  datesEditable = true,
  isSummaryParent = false,
  onCommitTaskDates,
}) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);
  const [actionMenuStep, setActionMenuStep] = useState<'list' | 'external'>('list');
  const [externalDraft, setExternalDraft] = useState('');
  const assigneeRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLDivElement>(null);
  const dragImageRef = useRef<HTMLElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const clearDragImage = useCallback(() => {
    dragImageRef.current?.remove();
    dragImageRef.current = null;
  }, []);

  useEffect(() => { setTitle(task.title); }, [task.title]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target as Node)) setShowAssigneeDropdown(false);
      if (actionRef.current && !actionRef.current.contains(e.target as Node)) setShowActionMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!showActionMenu) {
      setActionMenuStep('list');
      setExternalDraft('');
    }
  }, [showActionMenu]);

  useEffect(() => () => clearDragImage(), [clearDragImage]);

  const handleTitleSave = useCallback(() => {
    if (title.trim() && title.trim() !== task.title) {
      onUpdate(task.id!, { title: title.trim() });
    } else {
      setTitle(task.title);
    }
    setEditingTitle(false);
  }, [title, task, onUpdate]);

  const isCompleted = task.status === 'completed';
  const assigneeIds = normalizeTaskAssignedTo(task);
  const assignees = teamMembers.filter((u) => assigneeIds.includes(u.uid));
  const dueDate = task.endDate ? new Date(task.endDate) : null;
  const isOverdue = dueDate && !isCompleted && isTaskDueDatePastLocalCalendar(dueDate);
  const canEditDue =
    datesEditable && !isSummaryParent && !task.scheduleLocked && !!task.id;
  const needsPmReview = taskNeedsPmDateReview(task);

  const handleDueDateChange = useCallback(
    (iso: string) => {
      if (!task.id || !iso) return;
      const currentStart = task.startDate ? new Date(task.startDate) : new Date(`${iso}T12:00:00`);
      const end = new Date(`${iso}T12:00:00`);
      const start = end.getTime() < currentStart.getTime() ? new Date(end) : currentStart;
      if (onCommitTaskDates) {
        void onCommitTaskDates(task.id, start, end, { fromTaskPanel: true });
      } else {
        onUpdate(task.id, { startDate: start, endDate: end });
      }
    },
    [task.id, task.startDate, onCommitTaskDates, onUpdate],
  );

  const menuOpen =
    showAssigneeDropdown || showActionMenu || statusMenuOpen || priorityMenuOpen;

  return (
    <div
      className={`pm-task-row ${isCompleted ? 'pm-task-done' : ''} ${isOverdue ? 'pm-task-overdue' : ''} ${menuOpen ? 'pm-task-row--menu-open' : ''} ${isDragging ? 'pm-task-row--dragging' : ''}`}
      onDragOver={e => { e.preventDefault(); onDragOver?.(e); }}
      onDrop={e => onDrop?.(e, task)}
    >
      {draggable && (
        <div
          className="pm-task-grip"
          draggable
          title="Drag to reorder"
          onDragStart={e => {
            // Keep drag on the grip only. A draggable row steals mousedown/click from
            // status/priority/assignee menus, so table status changes never commit.
            e.dataTransfer.effectAllowed = 'move';
            try {
              e.dataTransfer.setData('text/plain', task.id || '');
            } catch {
              /* IE / some environments reject setData during certain gestures */
            }

            const handle = e.currentTarget as HTMLElement;
            const rowEl = handle.closest('.pm-task-row') as HTMLElement | null;
            if (rowEl && e.dataTransfer.setDragImage) {
              clearDragImage();
              const rect = rowEl.getBoundingClientRect();
              const clone = rowEl.cloneNode(true) as HTMLElement;
              clone.style.width = `${rect.width}px`;
              clone.style.boxSizing = 'border-box';
              clone.style.position = 'fixed';
              clone.style.left = '-9999px';
              clone.style.top = '0';
              clone.style.zIndex = '2147483647';
              clone.style.pointerEvents = 'none';
              clone.style.opacity = '0.98';
              clone.style.margin = '0';
              clone.classList.remove('pm-task-row--dragging');
              document.body.appendChild(clone);
              dragImageRef.current = clone;
              const offsetX = Math.round(e.clientX - rect.left);
              const offsetY = Math.round(e.clientY - rect.top);
              try {
                e.dataTransfer.setDragImage(clone, offsetX, offsetY);
              } catch {
                clearDragImage();
              }
            }

            setIsDragging(true);
            onDragStart?.(e, task);
          }}
          onDragEnd={() => {
            clearDragImage();
            setIsDragging(false);
          }}
        >
          <GripVertical size={14} />
        </div>
      )}

      <button
        className={`pm-task-check ${isCompleted ? 'pm-task-checked' : ''}`}
        onClick={() => onToggleComplete(task)}
      >
        {isCompleted && <Check size={12} strokeWidth={3} />}
      </button>

      <div className="pm-task-title-cell" onClick={() => !editingTitle && onSelect(task)}>
        {editingTitle ? (
          <input
            className="pm-inline-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={e => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') { setTitle(task.title); setEditingTitle(false); } }}
            autoFocus
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span
            className="pm-task-title"
            onDoubleClick={e => { e.stopPropagation(); setEditingTitle(true); }}
          >
            <span className="pm-task-title-text">{task.title}</span>
            {(subtaskTotal ?? task.subtasks?.length ?? 0) > 0 && (
              <span className="pm-task-subtask-count">
                {subtaskDone ?? task.subtasks?.filter(s => s.completed).length}/{subtaskTotal ?? task.subtasks?.length}
              </span>
            )}
          </span>
        )}
        <ChevronRight size={12} className="pm-task-arrow" />
      </div>

      <div
        className={`pm-task-assignee-cell${showAssigneeDropdown ? ' pm-dropdown-host--open' : ''}`}
        ref={assigneeRef}
      >
        <div className="pm-task-assignees" onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}>
          {assignees.length === 0 ? (
            <span className="pm-task-unassigned">—</span>
          ) : (
            <div className="pm-avatar-group">
              {assignees.slice(0, 3).map(u => (
                <UserAvatar
                  key={u.uid}
                  className="pm-avatar"
                  name={u.displayName}
                  photoURL={u.photoURL}
                  userId={u.uid}
                />
              ))}
            </div>
          )}
        </div>
        {showAssigneeDropdown && (
          <div className="pm-dropdown" style={{ minWidth: 200 }}>
            {teamMembers.map(u => {
              const isAssigned = assigneeIds.includes(u.uid);
              return (
                <button
                  key={u.uid}
                  className="pm-dropdown-item"
                  onClick={() => {
                    const newAssignees = isAssigned
                      ? assigneeIds.filter(id => id !== u.uid)
                      : [...assigneeIds, u.uid];
                    onUpdate(task.id!, { assignedTo: newAssignees });
                  }}
                >
                  <UserAvatar
                    className="pm-avatar"
                    name={u.displayName}
                    photoURL={u.photoURL}
                    userId={u.uid}
                    style={{ width: 20, height: 20, fontSize: 9 }}
                  />
                  <span style={{ flex: 1 }}>{u.displayName}</span>
                  {isAssigned && <Check size={14} style={{ color: 'var(--pm-accent)' }} />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className={`pm-task-status-cell${statusMenuOpen ? ' pm-dropdown-host--open' : ''}`}>
        <StatusPill
          status={task.status}
          onChange={s => onUpdate(task.id!, { status: s, progress: s === 'completed' ? 100 : task.progress })}
          onOpenChange={setStatusMenuOpen}
        />
      </div>

      {!hidePriority && (
      <div className={`pm-task-priority-cell${priorityMenuOpen ? ' pm-dropdown-host--open' : ''}`}>
        <PriorityBadge
          priority={task.priority}
          onChange={p => onUpdate(task.id!, { priority: p })}
          onOpenChange={setPriorityMenuOpen}
        />
      </div>
      )}

      <div
        className="pm-task-date-cell"
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        {needsPmReview && <PmDateReviewIcon />}
        {canEditDue ? (
          <label
            className={`pm-task-date-edit${isOverdue ? ' pm-date-overdue' : ''}`}
            title="Change due date"
          >
            <span className={`pm-task-date ${isOverdue ? 'pm-date-overdue' : ''}${!dueDate ? ' pm-date-empty' : ''}`}>
              {dueDate ? formatRelativeLocalCalendarDay(dueDate) : '—'}
            </span>
            <input
              type="date"
              className="pm-task-date-input"
              value={dueDate ? toLocalDateInputValue(dueDate) : ''}
              onChange={e => handleDueDateChange(e.target.value)}
              aria-label="Due date"
            />
          </label>
        ) : (
          <span className={`pm-task-date-static${needsPmReview ? ' pm-task-date-static--review' : ''}`}>
            <span className={`pm-task-date ${isOverdue ? 'pm-date-overdue' : ''}${!dueDate ? ' pm-date-empty' : ''}`}>
              {dueDate ? formatRelativeLocalCalendarDay(dueDate) : '—'}
            </span>
          </span>
        )}
      </div>

      {!hideAction && (
      <div
        className={`pm-task-action-cell${showActionMenu ? ' pm-dropdown-host--open' : ''}`}
        ref={actionRef}
        data-tour="task-action-required"
      >
        {task.actionRequired && task.status !== 'completed' ? (
          <div
            className={`pm-action-flag ${isActionRequiredExternal(task.actionRequired) ? 'pm-action-flag--external' : ''}`}
            onClick={() => setShowActionMenu(!showActionMenu)}
            title={
              isActionRequiredExternal(task.actionRequired)
                ? (task.actionRequiredNote || 'External — action required')
                : task.actionRequiredNote || 'Action required'
            }
          >
            <Flag size={10} />
            {getActionRequiredShortLabel(task.actionRequired, task.actionRequiredNote, teamMembers)}
          </div>
        ) : (
          <button
            className="pm-btn-ghost pm-btn-sm pm-action-set-btn"
            onClick={() => setShowActionMenu(!showActionMenu)}
            title="Set action required"
          >
            <Flag size={12} />
          </button>
        )}
        {showActionMenu && (
          <div className="pm-dropdown" style={{ right: 0, left: 'auto', minWidth: actionMenuStep === 'external' ? 220 : 180 }}>
            {actionMenuStep === 'external' ? (
              <div className="pm-action-external-panel" onClick={e => e.stopPropagation()}>
                <button
                  type="button"
                  className="pm-dropdown-item pm-action-external-back"
                  onClick={() => { setActionMenuStep('list'); setExternalDraft(''); }}
                >
                  <ArrowLeft size={12} /> Back
                </button>
                <label className="pm-action-external-label">Who we&apos;re waiting on</label>
                <input
                  className="pm-action-external-input"
                  value={externalDraft}
                  onChange={e => setExternalDraft(e.target.value)}
                  placeholder="e.g. county inspector, vendor"
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter' && externalDraft.trim()) {
                      onUpdate(task.id!, {
                        actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                        actionRequiredNote: externalDraft.trim(),
                      });
                      setShowActionMenu(false);
                    }
                  }}
                />
                <button
                  type="button"
                  className="pm-action-external-save"
                  disabled={!externalDraft.trim()}
                  onClick={() => {
                    if (!externalDraft.trim()) return;
                    onUpdate(task.id!, {
                      actionRequired: ACTION_REQUIRED_EXTERNAL_SENTINEL,
                      actionRequiredNote: externalDraft.trim(),
                    });
                    setShowActionMenu(false);
                  }}
                >
                  Save
                </button>
              </div>
            ) : (
              <>
                {task.actionRequired && (
                  <button
                    className="pm-dropdown-item"
                    onClick={() => { onUpdate(task.id!, { actionRequired: undefined, actionRequiredNote: undefined } as any); setShowActionMenu(false); }}
                  >
                    Clear Action Required
                  </button>
                )}
                {teamMembers.map(u => (
                  <button
                    key={u.uid}
                    className="pm-dropdown-item"
                    onClick={() => { onUpdate(task.id!, { actionRequired: u.uid, actionRequiredNote: undefined }); setShowActionMenu(false); }}
                  >
                    <Flag size={10} style={{ color: u.role === 'supplier' ? '#047857' : '#DC2626' }} />
                    {u.displayName}
                    {u.role === 'supplier' ? (
                      <span className="pm-dropdown-item-meta">{u.company?.trim() || 'Supplier'}</span>
                    ) : null}
                  </button>
                ))}
                <button
                  type="button"
                  className="pm-dropdown-item pm-dropdown-item--external"
                  onClick={() => {
                    setActionMenuStep('external');
                    setExternalDraft(
                      task.actionRequired && isActionRequiredExternal(task.actionRequired)
                        ? (task.actionRequiredNote || '').trim()
                        : '',
                    );
                  }}
                >
                  <Flag size={10} style={{ color: '#047857' }} />
                  External…
                </button>
              </>
            )}
          </div>
        )}
      </div>
      )}

      <div className="pm-task-progress-cell">
        <div className="pm-task-progress-bar">
          <div className="pm-task-progress-fill" style={{ width: `${task.progress}%` }} />
        </div>
        <span className="pm-task-progress-text">{task.progress}%</span>
      </div>
    </div>
  );
};

export default TaskRow;
